import { Fragment, useMemo, useState } from 'react'
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react'
import { buildBudgetOverview, formatDecimalHours } from '@coneno/c-sheet-shared'
import { ListToolbar } from '@/components/layout/list-toolbar'
import { PageBody, PageHeader } from '@/components/layout/page-header'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useProjects } from '@/hooks/use-projects'
import { useCustomers } from '@/hooks/use-customers'
import { useAllTasks } from '@/hooks/use-tasks'
import { useTimeEntries } from '@/hooks/use-timesheets'
import { matchesStatus, type StatusFilter } from '@/lib/status-filter'
import { cn } from '@/lib/utils'

function formatHours(minutes: number): string {
  return `${formatDecimalHours(minutes)} h`
}

function BudgetCells({
  trackedMinutes,
  budgetMinutes
}: {
  trackedMinutes: number
  budgetMinutes: number | null
}): React.JSX.Element {
  const remaining = budgetMinutes === null ? null : budgetMinutes - trackedMinutes
  const over = remaining !== null && remaining < 0
  const percentage = budgetMinutes ? Math.round((trackedMinutes / budgetMinutes) * 100) : 0
  const status = over
    ? 'over'
    : budgetMinutes !== null && trackedMinutes >= budgetMinutes * 0.9
      ? 'near'
      : 'within'
  return (
    <>
      <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
        {formatHours(trackedMinutes)}
      </td>
      <td className="px-3 py-2 text-right whitespace-nowrap text-muted-foreground tabular-nums">
        {budgetMinutes === null ? '–' : formatHours(budgetMinutes)}
      </td>
      <td
        className={cn(
          'px-3 py-2 text-right whitespace-nowrap tabular-nums',
          over && 'text-destructive'
        )}
      >
        {remaining === null ? '–' : `${over ? '−' : ''}${formatHours(Math.abs(remaining))}`}
      </td>
      <td className="px-3 py-2">
        {budgetMinutes === null ? (
          <Badge variant="outline">No budget</Badge>
        ) : (
          <div className="flex items-center gap-3">
            <div
              className="budget-meter h-1.5 min-w-20 flex-1 overflow-hidden rounded-full bg-muted"
              data-status={status}
              role="meter"
              aria-label="Hour budget used"
              aria-valuemin={0}
              aria-valuemax={Math.max(budgetMinutes, trackedMinutes, 1)}
              aria-valuenow={trackedMinutes}
              aria-valuetext={`${formatHours(trackedMinutes)} of ${formatHours(budgetMinutes)}${over ? ', over budget' : ''}`}
            >
              <div
                className="budget-meter-fill h-full rounded-full"
                style={{
                  width: `${budgetMinutes === 0 ? (over ? 100 : 0) : Math.min(100, (trackedMinutes / budgetMinutes) * 100)}%`
                }}
              />
            </div>
            <span
              className={cn('w-10 text-right text-xs tabular-nums', over && 'text-destructive')}
            >
              {budgetMinutes === 0 ? (over ? 'Over' : '0%') : `${percentage}%`}
            </span>
          </div>
        )}
      </td>
    </>
  )
}

export function OverviewPage(): React.JSX.Element {
  const projects = useProjects()
  const customers = useCustomers()
  const tasks = useAllTasks()
  const entries = useTimeEntries()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<StatusFilter>('active')
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  const names = useMemo(
    () => new Map(customers.data?.map((customer) => [customer.id, customer.name])),
    [customers.data]
  )
  const query = search.trim().toLowerCase()
  const { rows, totals } = useMemo(
    () =>
      buildBudgetOverview(
        (projects.data ?? []).filter(
          (project) =>
            matchesStatus(project.active, status) &&
            (!query ||
              [
                project.name,
                project.code,
                project.customerId ? names.get(project.customerId) : 'internal'
              ]
                .filter(Boolean)
                .some((value) => value!.toLowerCase().includes(query)))
        ),
        tasks.data ?? [],
        entries.data ?? []
      ),
    [projects.data, tasks.data, entries.data, status, query, names]
  )

  const error = projects.error ?? customers.error ?? tasks.error ?? entries.error
  const loading = projects.isPending || customers.isPending || tasks.isPending || entries.isPending
  function toggle(id: string): void {
    setExpanded((previous) => {
      const next = new Set(previous)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <PageBody>
      <PageHeader title="Budgets">
        <ListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search projects, clients, codes"
          status={status}
          onStatusChange={setStatus}
        />
      </PageHeader>

      {error ? (
        <p role="alert" className="text-sm text-destructive">
          Could not load the budgets: {error.message}
        </p>
      ) : loading ? (
        <Skeleton className="h-56 w-full" />
      ) : !rows.length ? (
        <p className="py-10 text-center text-muted-foreground">
          {projects.data?.length
            ? 'No projects match.'
            : 'Add your first project under Projects to start tracking its hour budget.'}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-card">
          <table className="w-full min-w-180 text-sm">
            <caption className="sr-only">
              Project and task hour budgets in decimal hours, rounded to one decimal place. Negative
              hours left indicate an overrun.
            </caption>
            <thead>
              <tr className="border-b bg-muted/40 text-xs text-muted-foreground">
                <th scope="col" className="px-3 py-2 text-left font-medium">
                  Project / task
                </th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  Tracked
                </th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  Budget
                </th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  Left
                </th>
                <th scope="col" className="w-[25%] px-3 py-2 text-left font-medium">
                  Used
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const open = expanded.has(row.id)
                const Chevron = open ? ChevronDownIcon : ChevronRightIcon
                return (
                  <Fragment key={row.id}>
                    <tr
                      className="cursor-pointer border-b hover:bg-muted/40"
                      onClick={() => toggle(row.id)}
                    >
                      <th scope="row" className="px-3 py-2 text-left font-medium">
                        <button
                          type="button"
                          aria-expanded={open}
                          aria-label={`${open ? 'Collapse' : 'Expand'} ${row.name}`}
                          className="flex w-full items-start gap-2 rounded-sm text-left focus-visible:outline-2 focus-visible:outline-ring"
                          onClick={(event) => {
                            event.stopPropagation()
                            toggle(row.id)
                          }}
                        >
                          <Chevron
                            className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                            aria-hidden="true"
                          />
                          <span>
                            {row.name}
                            <span className="font-normal text-muted-foreground">
                              {' '}
                              · {row.customerId ? names.get(row.customerId) : 'Internal'}
                            </span>
                            {!row.active && (
                              <Badge variant="secondary" className="ml-2">
                                Inactive
                              </Badge>
                            )}
                          </span>
                        </button>
                      </th>
                      <BudgetCells {...row} />
                    </tr>
                    {open && (
                      <>
                        {row.tasks.map((task) => (
                          <tr key={task.id} className="border-b bg-muted/30">
                            <th
                              scope="row"
                              className="py-2 pr-3 pl-9 text-left font-normal text-muted-foreground"
                            >
                              {task.name}
                              {!task.active && <span className="ml-2 text-xs">Inactive</span>}
                            </th>
                            <BudgetCells {...task} />
                          </tr>
                        ))}
                        {row.unassignedMinutes > 0 && (
                          <tr className="border-b bg-muted/30">
                            <th
                              scope="row"
                              className="py-2 pr-3 pl-9 text-left font-normal text-muted-foreground italic"
                            >
                              No task
                            </th>
                            <BudgetCells
                              trackedMinutes={row.unassignedMinutes}
                              budgetMinutes={null}
                            />
                          </tr>
                        )}
                        {!row.tasks.length && row.unassignedMinutes === 0 && (
                          <tr className="border-b bg-muted/30">
                            <td colSpan={5} className="py-2 pr-3 pl-9 text-muted-foreground">
                              No tasks or recorded time yet.
                            </td>
                          </tr>
                        )}
                      </>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
            <tfoot>
              <tr className="bg-muted/40 font-semibold">
                <th scope="row" className="px-3 py-2 text-left">
                  Total
                </th>
                <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                  {formatHours(totals.trackedMinutes)}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                  {totals.budgetedProjects ? formatHours(totals.budgetMinutes) : '–'}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                  {totals.budgetedProjects ? formatHours(totals.remainingMinutes) : '–'}
                </td>
                <td className="px-3 py-2 text-xs font-normal text-muted-foreground">
                  {totals.overProjects > 0 && (
                    <span className="block text-destructive">
                      {formatHours(totals.overMinutes)} over on {totals.overProjects}{' '}
                      {totals.overProjects === 1 ? 'project' : 'projects'}
                    </span>
                  )}
                  {totals.unbudgetedMinutes > 0 && (
                    <span className="block">
                      {formatHours(totals.unbudgetedMinutes)} without a budget
                    </span>
                  )}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </PageBody>
  )
}
